import { useEffect, useRef } from "react";
import styles from "./TurnstileWidget.module.css";

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SITE_KEY = "0x4AAAAAAEFKreqwlewy0BPo";
export const TURNSTILE_ACTION = "turnstile-spin-v2";

let turnstileScriptPromise;

const loadTurnstileScript = () => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }

    if (turnstileScriptPromise) {
        return turnstileScriptPromise;
    }

    turnstileScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);

        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(window.turnstile), { once: true });
            existingScript.addEventListener("error", reject, { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return turnstileScriptPromise;
};

const TurnstileWidget = ({ onVerify, onExpire, onError, resetSignal = 0 }) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const callbacksRef = useRef({ onVerify, onExpire, onError });

    callbacksRef.current = { onVerify, onExpire, onError };

    useEffect(() => {
        let isMounted = true;

        loadTurnstileScript()
            .then((turnstile) => {
                if (!isMounted || !containerRef.current || !turnstile) {
                    return;
                }

                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    action: TURNSTILE_ACTION,
                    theme: "light",
                    callback: (token) => callbacksRef.current.onVerify?.(token),
                    "expired-callback": () => callbacksRef.current.onExpire?.(),
                    "error-callback": () => callbacksRef.current.onError?.(),
                });
            })
            .catch(() => callbacksRef.current.onError?.());

        return () => {
            isMounted = false;

            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (window.turnstile && widgetIdRef.current) {
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetSignal]);

    return (
        <div
            className={`cf-turnstile ${styles.widget}`}
            data-sitekey={TURNSTILE_SITE_KEY}
            data-action={TURNSTILE_ACTION}
            ref={containerRef}
        />
    );
};

export default TurnstileWidget;
